'use client';

import { buttonVariants } from '@/components/ui/button';
import { siteConfig } from '@/site-config';
import { CustomLink } from '@veranda/shared/components/custom-link';
import {
  ProductButtons,
  ProductDetailsActionBar,
  ProductDetailsBackInStockForm,
} from '@veranda/shared/components/pages/product-details';
import type { ProductPageProps } from '@veranda/shared/components/pages/product-details/client';
import { cn } from '@veranda/shared/lib';
import { SanityImage } from '@veranda/shared/sanity/utils/components';
import type { CSSProperties } from 'react';

export function ActionBar({
  product,
}: {
  product: ProductPageProps;
}) {
  const squareMetersPerBox = product.squareMetersPerBox;
  const color = product.color;
  const size = product.attributes?.find(
    (attribute) => attribute?.title === 'Size'
  )?.value;
  const finish = product.attributes?.find(
    (attribute) => attribute?.title === 'Finish'
  )?.value;

  return (
    <ProductDetailsActionBar.Container
      style={{ '--color': 'hsl(var(--color-brand-burgundy))' } as CSSProperties}
    >
      <ProductDetailsActionBar.Section>
        <ProductDetailsActionBar.Links>
          {product.defaultSettings?.actionBar?.map((link) => (
            <CustomLink
              key={link._key}
              href={link.slug ?? ''}
              target={link.openInNewTab ? '_blank' : '_self'}
              className="mt-px flex h-full items-center ac:border-b-primary border-b-2 border-b-transparent hf:border-b-primary pt-0.5 font-clash font-medium text-primary sm:whitespace-nowrap"
            >
              {link.text}
            </CustomLink>
          ))}
        </ProductDetailsActionBar.Links>
        <ProductDetailsActionBar.Main>
          <ProductDetailsActionBar.ImageContainer>
            {product.mainImage && (
              <SanityImage
                image={product.mainImage}
                width={50}
                height={50}
                className="inset-0 block h-full w-full object-cover object-center"
              />
            )}
          </ProductDetailsActionBar.ImageContainer>
          <ProductDetailsActionBar.ScrollToTopButton
            title={product.title ?? ''}
            details={[
              squareMetersPerBox && `${squareMetersPerBox}m²`,
              color,
              finish,
              size,
            ]
              .filter(Boolean)
              .join(' / ')}
          />
          <ProductDetailsActionBar.Buttons>
            {product.productGid && product.variants?.at(0)?.gid && (
              <ProductButtons.ProductButtons
                product={product}
                productSlug={product.slug}
                backInStockForm={
                  <ProductDetailsBackInStockForm
                    variantGid={product.variants?.[0]?.gid ?? ''}
                    titleClassName="typography-clash-h3 text-primary font-medium tracking-wide"
                    descriptionClassName="typography-satoshi-body text-primary"
                    triggerButtonClassNames={cn(
                      buttonVariants({ size: 'sm' }),
                      'w-full'
                    )}
                    submitButtonClassNames={cn(
                      buttonVariants({ size: 'sm' }),
                      'h-auto w-full'
                    )}
                  />
                }
                domain={siteConfig.baseUrl}
                addToCartButtonClassName={cn(
                  buttonVariants({ size: 'sm' }),
                  'w-full'
                )}
                sampleButtonClassName={cn(
                  buttonVariants({ variant: 'primary-outline', size: 'sm' }),
                  'w-full'
                )}
                className="flex gap-2"
                addToCartContainerClassName="flex-1"
                sampleContainerClassName="flex-1"
              />
            )}
          </ProductDetailsActionBar.Buttons>
        </ProductDetailsActionBar.Main>
      </ProductDetailsActionBar.Section>
    </ProductDetailsActionBar.Container>
  );
}
